import {
    IonPage,
    IonGrid,
    IonRow,
    IonCol,
    isPlatform,
} from '@ionic/react';
import { Route, Redirect } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from './caremap/CareMapState';
import { useAsyncEffect } from './support/useAsyncEffect';
import { ReactFlowProvider } from 'reactflow';
import { AppHeader } from './AppHeader';
import { CareMapDrawer } from './caremap/CareMapDrawer';
import { SupportGroupMemberList } from './support-group/SupportGroupMemberList';
import { SupportGroupMemberEdit } from './support-group/SupportGroupMemberEdit';
import { CaremapEnqueueErrorToaster } from './caremap/CaremapEnqueueErrorToaster';
import { SupportGroupMemberView } from './support-group/SupportGroupMemberView';
import { AppPrincipal, isCaremapViewer } from './auth/AppPrincipal';
import { HouseholdMemberList } from './household/HouseholdMemberList';
import { HouseholdMemberView } from './household/HouseholdMemberView';
import { HouseholdMemberEdit } from './household/HouseholdMemberEdit';
import { CareMapDrawerState } from './caremap/CareMapDrawerState';
import { PatientEdit } from './household/PatientEdit';
import { PatientView } from './household/PatientView';
import { TrackPageView } from './metrics/TrackPageView';
import { DismissableNudgeBanner } from './components/DismissableNudgeBanner';
import { SearchIcon } from './theme/Icons';
import { useBreakpoints } from './theme/useBreakpoints';
import { DebugPage } from './support/DebugPage';
import { LiveUpdatesListener } from '@meraki-internal/live-updates';
import { IntercomService } from './support/IntercomService';
import { HistoryViewModel } from './support/HistoryViewModel';
import React from 'react';
import { NotesView } from './notes/NotesView';
import { CaremapNoteEditor } from './notes/CaremapNoteEditor';
import { ISupportGroupKey } from './caremap/ICareMap';
import { NavigationMenu } from './navigation/NavigationMenu';
import { CareMapPage } from './caremap/CareMapPage';
import { FeatureFlags } from './support/feature-flags/FeatureFlags';
import { InvitationAcceptancePage } from './share-caremap/InvitationAcceptancePage';
import { QuestionnaireRouteHandler } from './wizard/QuestionnaireRouteHandler';
import { AlertPresenter } from './AlertBinder';
import { InnerhivePlusDialog } from './innerhive-plus/InnerhivePlusDialog';
import { AppointmentsView } from './appointments/AppointmentsView';
import { AppointmentEditorDrawer } from './appointments/ApointmentEditorDrawer';
import { useDismissOverlays } from './utils/useDismissOverlays';
import { ConnectCalendarModal } from './appointments/ConnectCalendarModal';
import { HelpCenterArticlesView } from './support/help-center/HelpCenterArticlesView';

const useStyles = createUseStyles({
    grid: {
        padding: 0,
        width: '100%',

        '--ion-grid-column-padding-xs': 0,
        '--ion-grid-column-padding-sm': 0,

        '& > ion-row': {
            height: '100%'
        }
    },
});

export const InnerhiveApp: React.FC = () => {
    const isViewer = isCaremapViewer(useInstance(AppPrincipal));

    const classes = useStyles();
    const {isAboveBreakpoint, isBelowBreakpoint} = useBreakpoints();

    const careMapStateInstance = useInstance(CareMapState);
    useSubscription(() => careMapStateInstance);

    const drawerState = useInstance(CareMapDrawerState);
    useSubscription(() => drawerState);

    const intercomService = useInstance(IntercomService);
    const historyViewModel = useInstance(HistoryViewModel);

    const alert = useInstance(AlertPresenter);

    // load the CareMap data from the server
    // TODO: possibly try / catch to do something custom with the UI / UX
    useAsyncEffect(careMapStateInstance.load, []);

    const dismissOverlays = useDismissOverlays();

    if (!careMapStateInstance.state) {
        return null;
    }

    const isInvite = historyViewModel.getCurrentLocation().pathname === '/accept-invite';
    const pageTitle = (careMapStateInstance.state.essentialMapComplete ?
        `${isAboveBreakpoint('md') ? careMapStateInstance.getPatientFullName()+'\'s' : ''} Care Map`
        : isInvite ? <img height="24px" src="/assets/images/logos/logo-with-text-white.svg" alt="logo" /> : 'Questionnaire'
    );

    return (

        <IonPage>
            <LiveUpdatesListener />
            <AppHeader title={pageTitle} />
            <CaremapEnqueueErrorToaster />
            <InnerhivePlusDialog />
            <ConnectCalendarModal />
            <DebugPage />

            <Route exact path="/accept-invite" render={(routeProps) => {
                const params = new URLSearchParams(routeProps.location.search);
                return (
                    <InvitationAcceptancePage invite={params.get('invite') || undefined} />
                );
            }} />

            <IonGrid className={classes.grid} id="innerhive-grid">
                <IonRow>
                    <Route exact path="/tour/:id" render={(routeProps) => {
                        const id: number = parseInt(routeProps.match.params.id);
                        const params = new URLSearchParams(routeProps.location.search);
                        const path: string = params.get('path') || 'caremaps';
                        intercomService.startTour(id);
                        historyViewModel.replace(`/#/${path}`);
                        return undefined;
                    }} />

                    {/* capture legacy /caremap/ routes and redirect to new /caremaps/ */}
                    <Route path="/caremap">
                        <Redirect to="/caremaps" />
                    </Route>

                    {/* TODO: will change when we have a list of care maps UX */}
                    <Route path="/caremaps" exact render={() => {
                        if (careMapStateInstance.state?.careMapId){
                            return <Redirect to={`/caremaps/${careMapStateInstance.state.careMapId}`} />;
                        }
                        else {
                            return <Redirect to="questionnaire" />;
                        }
                    }} />

                    <Route path="/caremaps/:careMapId" render={(routeProps) => {
                        const careMapId = routeProps.match.params.careMapId;
                        const careMap = careMapStateInstance.getCareMapById(careMapId);
                        if (!careMap){
                            alert.showAlertV2({
                                header: 'Care Map Not Found',
                                message: `We're sorry, the care map you were previously viewing has been deleted, or you no longer have access to it.`
                            });
                            return <Redirect to="/" />;
                        }

                        if (!isViewer && !careMap.essentialMapComplete) {
                            return <Redirect to="/questionnaire" />;
                        }

                        return (
                            <IonCol size="12">
                                {isBelowBreakpoint('md') &&
                                    <DismissableNudgeBanner
                                        nudgeId="larger-screen"
                                        Icon={SearchIcon}
                                        message={`Zoom out to see full map, zoom in for detail. ${isPlatform('capacitor') ? 'You can also use on a computer or tablet!' : 'Try a larger screen for the best experience.'}`}
                                    />
                                }
                                <ReactFlowProvider>
                                    <CareMapPage careMapId={careMapId} />
                                </ReactFlowProvider>
                            </IonCol>
                        );
                    }}>
                    </Route>

                    {/* careMapId is optional, in order to catch old / bookmarked urls */}
                    <Route path="/questionnaire/:careMapId?" render={(props) => {
                        return <QuestionnaireRouteHandler careMapId={props.match.params.careMapId} />;
                    }}>
                    </Route>
                </IonRow>
            </IonGrid>

            <CareMapDrawer
                basePath="/caremaps/:careMapId/drawer/household"
            >
                <Route exact path="/caremaps/:careMapId/drawer/household" render={() => (
                    <>
                        <TrackPageView page="Household List" />
                        <HouseholdMemberList />
                    </>
                )} />

                <Route exact path="/caremaps/:careMapId/drawer/household/member/:memberId" render={(routeProps) => {
                    const memberId = routeProps.match.params.memberId;
                    if (memberId === 'patient') {
                        return (
                            <>
                                <TrackPageView page="Care Recipient Details" />
                                <PatientView />
                            </>
                        );
                    }
                    return (
                        <>
                            <TrackPageView page="Household Member Details" />
                            <HouseholdMemberView memberId={memberId} />
                        </>
                    );
                }} />

                <Route exact path="/caremaps/:careMapId/drawer/household/member/:memberId/edit" render={(routeProps) => {
                    if (isViewer) {
                        const careMapId = routeProps.match.params.careMapId;
                        return <Redirect to={`/caremaps/${careMapId}`} />;
                    }
                    const memberId = routeProps.match.params.memberId;
                    if (memberId === 'patient') {
                        return <PatientEdit />;
                    }
                    return <HouseholdMemberEdit memberId={memberId} />;
                }} />

                <Route exact path="/caremaps/:careMapId/drawer/household/add-member" render={(routeProps) => {
                    const careMapId = routeProps.match.params.careMapId;
                    return isViewer ? <Redirect to={`/caremaps/${careMapId}`} /> :  <HouseholdMemberEdit />;
                }} />

                <Route exact path="/caremaps/:careMapId/drawer/household/notes" render={() => {
                    return (
                        <NotesView
                            titlePrefix="Household"
                            noteTarget={{section: 'household'}} />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/household/notes/:noteId" render={(routeProps) => {
                    const noteId = routeProps.match.params.noteId;
                    return (
                        <CaremapNoteEditor
                            noteId={noteId}
                            noteTarget={{section: 'household'}}
                        />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/household/notes/add-note" render={(routeProps) => {
                    return (
                        <CaremapNoteEditor
                            noteId=""
                            noteTarget={{section: 'household'}}
                        />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/household/member/:memberId/notes" render={(routeProps) => {
                    const memberId = routeProps.match.params.memberId;
                    const isPatient = memberId === 'patient';
                    const titlePrefix = isPatient ? 'Care Recipient' : 'Household Member';

                    return (
                        <NotesView
                            titlePrefix={titlePrefix}
                            noteTarget={{section: isPatient ? 'patient' : 'household', memberId: isPatient ? '' : memberId}}
                        />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/household/member/:memberId/notes/:noteId" render={(routeProps) => {
                    const {memberId} = routeProps.match.params;
                    const isPatient = memberId === 'patient';
                    const {noteId} = routeProps.match.params;

                    return (
                        <CaremapNoteEditor
                            noteId={noteId === 'add-note' ? '' : noteId}
                            noteTarget={{section: isPatient ? 'patient' : 'household', memberId: isPatient ? '' : memberId}}
                        />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/household/member/patient/appointments" render={(routeProps) => {
                    return <AppointmentsView />;
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/household/member/patient/appointments/:appointmentId" render={(routeProps) => {
                    dismissOverlays();

                    const {appointmentId} = routeProps.match.params;
                    return (
                        <AppointmentEditorDrawer
                            from={'care recipient'} // tracking
                            appointmentId={appointmentId}
                        />
                    );
                }} />
            </CareMapDrawer>

            <CareMapDrawer
                basePath="/caremaps/:careMapId/drawer/support-group/:supportGroupKey"
            >
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;

                    // if we're not supposed to show it, don't allow a deep link to it
                    if (!careMapStateInstance.getSupportGroup(supportGroupKey).isRelevant) {
                        return <Redirect to="/" />;
                    }

                    return (
                        <>
                            <TrackPageView page="Care Member List" meta={{ category: routeProps.match.params.supportGroupKey }} />
                            <SupportGroupMemberList supportGroupKey={supportGroupKey} />
                        </>
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/notes" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const titlePrefix = careMapStateInstance.getSupportGroupLabels(supportGroupKey).title;

                    return (
                        <NotesView
                            titlePrefix={titlePrefix}
                            noteTarget={{section: supportGroupKey}} />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/notes/:noteId" render={(routeProps) => {
                    const {supportGroupKey} = routeProps.match.params;
                    const {noteId} = routeProps.match.params;

                    return (
                        <CaremapNoteEditor
                            noteId={noteId === 'add-note' ? '' : noteId}
                            noteTarget={{section: supportGroupKey as ISupportGroupKey}}
                        />
                    );
                }} />

                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/appointments" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    return <AppointmentsView supportGroupKey={supportGroupKey} />;
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/appointments/:appointmentId" render={(routeProps) => {
                    dismissOverlays();

                    const {appointmentId} = routeProps.match.params;
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;

                    return (
                        <AppointmentEditorDrawer
                            from={supportGroupKey}
                            supportGroupKey={supportGroupKey}
                            appointmentId={appointmentId}
                        />
                    );
                }} />

                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const memberId = routeProps.match.params.memberId;
                    return (
                        <>
                            <TrackPageView page="Care Member Details" />
                            <SupportGroupMemberView supportGroupKey={supportGroupKey} memberId={memberId} />
                        </>
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId/edit" render={(routeProps) => {
                    if (isViewer) {
                        const careMapId = routeProps.match.params.careMapId;
                        return <Redirect to={`/caremaps/${careMapId}`} />;
                    }
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const memberId = routeProps.match.params.memberId;
                    return (
                        <SupportGroupMemberEdit supportGroupKey={supportGroupKey} memberId={memberId} />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/add-member" render={(routeProps) => {
                    if (isViewer) {
                        const careMapId = routeProps.match.params.careMapId;
                        return <Redirect to={`/caremaps/${careMapId}`} />;
                    }
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    return (
                        <SupportGroupMemberEdit supportGroupKey={supportGroupKey} />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId/notes" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const titlePrefix = careMapStateInstance.getSupportGroupLabels(supportGroupKey).memberTitle;
                    const memberId = routeProps.match.params.memberId;

                    return (
                        <NotesView
                            titlePrefix={titlePrefix}
                            noteTarget={{section: supportGroupKey, memberId: memberId}}
                        />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId/notes/:noteId" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    const {memberId} = routeProps.match.params;
                    const {noteId} = routeProps.match.params;

                    return (
                        <CaremapNoteEditor
                            noteId={noteId === 'add-note' ? '' : noteId}
                            noteTarget={{section: supportGroupKey, memberId: memberId}}
                        />
                    );
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId/appointments" render={(routeProps) => {
                    const {memberId} = routeProps.match.params;
                    return <AppointmentsView memberId={memberId} />;
                }} />
                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/member/:memberId/appointments/:appointmentId" render={(routeProps) => {
                    dismissOverlays();

                    const {appointmentId} = routeProps.match.params;
                    const {memberId} = routeProps.match.params;
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;

                    return (
                        <AppointmentEditorDrawer
                            from={`${supportGroupKey} member`}
                            supportMemberId={memberId}
                            supportGroupKey={supportGroupKey}
                            appointmentId={appointmentId}
                        />
                    );
                }} />

                <Route exact path="/caremaps/:careMapId/drawer/support-group/:supportGroupKey/help-center/articles" render={(routeProps) => {
                    const supportGroupKey = routeProps.match.params.supportGroupKey as ISupportGroupKey;
                    return <HelpCenterArticlesView supportGroupKey={supportGroupKey} />;
                }} />
            </CareMapDrawer>

            <NavigationMenu />

            <Route exact path="/feature-flags">
                <FeatureFlags />
            </Route>

            <Route exact path="/">
                <Redirect to="/caremaps" />
            </Route>
        </IonPage>
    );
};
